/**
 * What this tab last served for each resource, and the first-cache-write event.
 *
 * Freshness is in-memory and per tab: it answers "is the list on screen a
 * cached copy, and from when?" so a badge can say so. The first-write event
 * backs the one-time notice that this device now keeps a readable copy; it is
 * made durable per account in the store's `meta` so it fires once, ever.
 */

import { getMeta, setMeta } from './store';

export interface Freshness {
  /** When the served data was fetched from the server. */
  fetchedAt: number;
  /** True when the data on screen came from the cache, not the network. */
  servedFromCache: boolean;
  /**
   * True when the served copy is older than its policy's ttl (a revalidation
   * is pending or failed) or came from a network-first fallback.
   */
  stale: boolean;
}

type FreshnessListener = (freshness: Freshness) => void;

const NOTICE_META_KEY = 'noticeShown';

const freshness = new Map<string, Freshness>();
const freshnessListeners = new Map<string, Set<FreshnessListener>>();
const firstWriteListeners = new Set<() => void>();
const noticedAccounts = new Set<string>();

/**
 * A first write that landed with nothing listening yet, held for the next
 * subscriber. The read path caches during the first render, which can beat the
 * notice component's mount; without this the one showing would be lost.
 */
let pendingFirstWriteNotice = false;

/**
 * Bumped by every reset, so a notice whose store write was already in flight
 * cannot be held for after the reset that was meant to clear it.
 */
let resetGeneration = 0;

/** The last-served freshness for a key, or null if nothing was served this tab. */
export function getFreshness(key: string): Freshness | null {
  return freshness.get(key) ?? null;
}

/** Record what was just served for a key and tell subscribers. */
export function recordFreshness(key: string, next: Freshness): void {
  freshness.set(key, next);
  freshnessListeners.get(key)?.forEach(listener => {
    try {
      listener(next);
    } catch {
      // A broken subscriber must not stop the others.
    }
  });
}

/** Be told whenever freshness for a key changes. Returns a disposer. */
export function subscribeToFreshness(key: string, listener: FreshnessListener): () => void {
  const set = freshnessListeners.get(key) ?? new Set<FreshnessListener>();
  set.add(listener);
  freshnessListeners.set(key, set);
  return () => {
    set.delete(listener);
    if (set.size === 0) freshnessListeners.delete(key);
  };
}

/**
 * Be told the first time this device caches anything for the account.
 *
 * A write that already happened with nothing listening is replayed to the
 * first subscriber to arrive, and only to that one.
 */
export function subscribeToFirstCacheWrite(listener: () => void): () => void {
  firstWriteListeners.add(listener);
  if (pendingFirstWriteNotice) {
    pendingFirstWriteNotice = false;
    listener();
  }
  return () => {
    firstWriteListeners.delete(listener);
  };
}

/**
 * Called by the read path after a successful cache write.
 *
 * Fires the event once per account: an in-memory guard for this tab, and the
 * `meta` row for every later tab and boot.
 */
export async function noteFirstCacheWrite(account: string): Promise<void> {
  if (noticedAccounts.has(account)) return;
  noticedAccounts.add(account);
  const generation = resetGeneration;
  if (await getMeta<boolean>(NOTICE_META_KEY, account)) return;
  if (!(await setMeta(NOTICE_META_KEY, true, account))) {
    // Nothing was recorded, so nothing was kept: a notice promising a readable
    // copy would be a lie, and the next write deserves another chance to fire.
    noticedAccounts.delete(account);
    return;
  }
  // A reset while the store write was in flight already declared this tab to
  // have noticed nothing; announcing it now would contradict that.
  if (generation !== resetGeneration) return;
  if (firstWriteListeners.size === 0) {
    pendingFirstWriteNotice = true;
    return;
  }
  firstWriteListeners.forEach(listener => {
    try {
      listener();
    } catch {
      // A broken subscriber must not stop the others.
    }
  });
}

/** Tests start from nothing served and nothing noticed. */
export function resetFreshnessForTests(): void {
  freshness.clear();
  freshnessListeners.clear();
  firstWriteListeners.clear();
  noticedAccounts.clear();
  pendingFirstWriteNotice = false;
  resetGeneration += 1;
}
